import type { EventUnsubscribe } from '@spotar/runtime';
import type AudioPlayer from '../audioplayer';

let _audioPlayer: AudioPlayer | null = null;

const detachNothing: EventUnsubscribe = () => {};

export function initOnPlayerEventsImplementation(audioPlayer: AudioPlayer) {
  _audioPlayer = audioPlayer;
}

function attachPlayerEventListener(
  eventName: string,
  triggerOutPortFunctions: Map<string, () => void>,
  shouldTrigger: (player: AudioPlayer) => boolean = () => true
): EventUnsubscribe {
  const player = _audioPlayer;
  if (!player) return detachNothing;

  const listener = () => {
    if (!shouldTrigger(player)) return;
    triggerOutPortFunctions.get('exec')?.();
  };

  player.addEventListener(eventName, listener);
  return () => player.removeEventListener(eventName, listener);
}

function combineUnsubscribes(...unsubscribes: EventUnsubscribe[]): EventUnsubscribe {
  return () => {
    for (const unsubscribe of unsubscribes) unsubscribe();
  };
}

function matchesBoundAudio(player: AudioPlayer, data: Record<string, unknown>) {
  const audio = data.audio as { url?: string } | undefined;
  if (audio == undefined || player.currentAudioUrl !== audio.url) return false;
  return true;
}

function hasLegacyBoundAudio(data: Record<string, unknown>) {
  return Object.hasOwn(data, 'audio') && data.audio != null;
}

export function runOnPlayerPlayImplementation(
  data: Record<string, unknown>,
  triggerOutPortFunctions: Map<string, () => void>
) {
  // Legacy graphs saved before the AndAudioActive split may still carry `audio`.
  if (hasLegacyBoundAudio(data)) {
    return runOnPlayerPlayAndAudioActiveImplementation(data, triggerOutPortFunctions);
  }
  // AudioPlayer emits `playStarted` on first play and `resumed` after pause.
  return combineUnsubscribes(
    attachPlayerEventListener('playStarted', triggerOutPortFunctions),
    attachPlayerEventListener('resumed', triggerOutPortFunctions)
  );
}

export function runOnPlayerPlayAndAudioActiveImplementation(
  data: Record<string, unknown>,
  triggerOutPortFunctions: Map<string, () => void>
) {
  const shouldTrigger = (player: AudioPlayer) => matchesBoundAudio(player, data);
  return combineUnsubscribes(
    attachPlayerEventListener('playStarted', triggerOutPortFunctions, shouldTrigger),
    attachPlayerEventListener('resumed', triggerOutPortFunctions, shouldTrigger)
  );
}

export function runOnPlayerTrackFinishedImplementation(
  data: Record<string, unknown>,
  triggerOutPortFunctions: Map<string, () => void>
) {
  if (hasLegacyBoundAudio(data)) {
    return runOnPlayerTrackFinishedAndAudioActiveImplementation(data, triggerOutPortFunctions);
  }
  return attachPlayerEventListener('finished', triggerOutPortFunctions);
}

export function runOnPlayerTrackFinishedAndAudioActiveImplementation(
  data: Record<string, unknown>,
  triggerOutPortFunctions: Map<string, () => void>
) {
  return attachPlayerEventListener('finished', triggerOutPortFunctions, (player) =>
    matchesBoundAudio(player, data)
  );
}

export function runOnPlayerPausedImplementation(
  data: Record<string, unknown>,
  triggerOutPortFunctions: Map<string, () => void>
) {
  if (hasLegacyBoundAudio(data)) {
    return runOnPlayerPausedAndAudioActiveImplementation(data, triggerOutPortFunctions);
  }
  return attachPlayerEventListener('stopped', triggerOutPortFunctions);
}

export function runOnPlayerPausedAndAudioActiveImplementation(
  data: Record<string, unknown>,
  triggerOutPortFunctions: Map<string, () => void>
) {
  return attachPlayerEventListener('stopped', triggerOutPortFunctions, (player) =>
    matchesBoundAudio(player, data)
  );
}
